import { Show } from "shared/components";
import { formatPhone } from "shared/utils";
import { employerDeclineReasonLabel } from "shared/app/EmployerVerification/declineReasons";
import { recipientChanged } from "shared/app/EmployerVerification/supervisorContact";
import { RoleTitles } from "views/settings-users/slices/users/constants";
import type {
  EmployerVerificationDetails,
  EmployerVerificationSurveyActivity
} from "../types";

/**
 * BSCPF-6584. Detail lines under an employer verification timeline entry.
 *
 * Mirrors PremiumRentalVerificationSurveyView, but reads the supervisor off
 * `employer_details` rather than a landlord.
 */

interface Props {
  activityItem: EmployerVerificationSurveyActivity;
}

const supervisorName = (details?: EmployerVerificationDetails) => {
  const name = [details?.first_name, details?.last_name]
    .filter(Boolean)
    .join(" ");

  return name || undefined;
};

// The API stores the phone as bare E.164 digits; `formatPhone` is what the rest of the
// portal renders a number with, so the timeline matches the report and the contacts table.
const contactLine = (details?: EmployerVerificationDetails) =>
  [details?.email, details?.phone ? formatPhone(details.phone) : undefined]
    .filter(Boolean)
    .join(" · ") || undefined;

const AuthorLine = ({
  author
}: {
  author?: EmployerVerificationSurveyActivity["author"];
}) => {
  if (!author?.name) return null;
  const role = author.author_role ? RoleTitles[author.author_role] : null;

  return (
    <p className="text-sm font-normal text-content-400">
      by {author.name}
      {role ? `, ${role}` : ""}
    </p>
  );
};

export const EmployerVerificationSurveyView = ({ activityItem }: Props) => {
  if (
    activityItem.activity_type === "employment_verification_survey_sent" ||
    activityItem.activity_type === "employment_verification_survey_resent"
  ) {
    const details = activityItem.details?.employer_details;

    return (
      <>
        <Show if={supervisorName(details)}>
          <p className="text-sm font-normal text-content-1000">
            To {supervisorName(details)}
            {details?.company_name ? ` at ${details.company_name}` : ""}
          </p>
        </Show>
        <Show if={contactLine(details)}>
          <p className="text-sm font-normal text-content-400">
            {contactLine(details)}
          </p>
        </Show>
        <AuthorLine author={activityItem.author} />
      </>
    );
  }

  if (
    activityItem.activity_type ===
    "employment_verification_supervisor_details_updated"
  ) {
    const before = activityItem.details?.original_employer_details;
    const after = activityItem.details?.updated_employer_details;
    // The modal always sends all four fields, so "it was in the payload" is not a change.
    // Only strike the old contact through when the recipient actually moved — a name-only
    // correction would otherwise render the same line twice, once crossed out. This is
    // the same comparison the server used to decide whether to clear the answers, so the
    // strike-through and the reset never disagree.
    const contactChanged = recipientChanged(before, after);
    const nameChanged = supervisorName(before) !== supervisorName(after);

    return (
      <>
        <Show if={nameChanged && supervisorName(after)}>
          <p className="text-sm font-normal text-content-1000">
            Now {supervisorName(after)}
          </p>
        </Show>
        <Show if={contactChanged && contactLine(before)}>
          <p className="text-sm font-normal text-content-400 line-through">
            {contactLine(before)}
          </p>
        </Show>
        <Show if={contactLine(after)}>
          <p className="text-sm font-normal text-content-1000">
            {contactLine(after)}
          </p>
        </Show>
        <AuthorLine author={activityItem.author} />
      </>
    );
  }

  if (
    activityItem.activity_type === "employment_verification_survey_declined"
  ) {
    const reason = activityItem.details?.decline_reason;
    const extra = activityItem.details?.decline_reason_details;
    // Both channels, because a supervisor reached by SMS alone has no email to show and
    // the entry would otherwise name nobody.
    const contact = contactLine({
      email: activityItem.details?.employer_email,
      phone: activityItem.details?.employer_phone
    });

    return (
      <>
        <Show if={reason}>
          <p className="text-sm font-normal text-content-1000">
            {reason ? employerDeclineReasonLabel(reason) : null}
          </p>
        </Show>
        <Show if={extra}>
          <p className="text-sm font-normal text-content-400">{extra}</p>
        </Show>
        <Show if={contact}>
          <p className="text-sm font-normal text-content-400">{contact}</p>
        </Show>
      </>
    );
  }

  return null;
};
